/**
 * The feature's texts — the fields and the JSON, as one document in two views.
 *
 * Same arrangement as What's New's step 2, deliberately: editing a field rewrites the JSON, and
 * pasting a valid document repopulates the fields. Invalid JSON is left exactly as typed with the
 * parser's message in the status, because reverting someone's text mid-edit is worse than showing
 * them the error.
 *
 * Field labels are the JSON keys, in mono. That is what makes the two modes line up one to one in
 * the head of whoever is using them.
 *
 * The document carries the fifteen texts in the three languages, plus the entry point's appearance
 * and the two toast switches — which have no language and therefore no tab. Identity — BU, key,
 * use cases, kill switch — stays
 * in the block above: a key arriving from a pasted document is the easiest way to register a
 * feature the dashboard will never find. What the person answers on the way out is not here
 * either: it is the exit questionnaire, which is a survey.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTTextArea, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { OptInEntryAppearanceFields } from './OptInEntryAppearanceFields';
import { OptInEntryPreview } from './OptInEntryPreview';
import type { OptInPreviewSurface } from './OptInModalPreview';
import { OptInToastFields } from './OptInToastFields';
import type { OptInLanguage } from '../../../Types/OptInFeatures.types';
import { OPT_IN_LANGUAGES, OPT_IN_LANGUAGE_LABELS } from '../../../Types/OptInFeatures.types';
import type {
  OptInAppearanceDraft,
  OptInFeatureFormData,
  OptInFieldWidth,
  OptInTextsDraft,
  OptInToastsDraft,
} from '../../../Utils/optInFeatureForm';
import { OPT_IN_TEXT_SURFACES, OPT_IN_TOAST_SIDES } from '../../../Utils/optInFeatureForm';
import { createTextsPrompt, createTextsTemplate } from '../../../Utils/textsJson';
import { useActionToast } from '@/Hooks/useActionToast';
import { SchemaDialog } from '@/Shared/components/SchemaDialog/SchemaDialog';
import { Tag } from '@/Shared/components/Tag/Tag';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const titleRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  min-width: 0;
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const subtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const controlsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  padding-bottom: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const dividerStyle = css`
  width: 1px;
  height: 22px;
  background: var(--color-divider);
`;

const miniLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const statusStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-2xs);
  max-width: 340px;
`;

const jsonModeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const jsonActionsStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const jsonAreaStyle = css`
  width: 100%;
  height: 520px;
  padding: var(--space-m);
  border-radius: var(--radius-m);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

/** What is still missing, listed rather than hidden behind a disabled button. */
const pendingStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const pendingTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const pendingListStyle = css`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const surfaceStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const surfaceHeadStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const surfaceIndexStyle = css`
  font-size: var(--font-size-md);
  color: var(--color-primary);
`;

const surfaceTitleStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const surfaceHintStyle = css`
  margin: 0;
  flex: 1 1 260px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const fieldsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SPAN_BY_WIDTH: Record<OptInFieldWidth, string> = {
  cta: 'span 3',
  title: '1 / span 5',
  full: '1 / -1',
};

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** The JSON key, so the two modes read as the same document. */
const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const fieldNameStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const proseFieldStyle = css`
  max-width: 68ch;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

export interface OptInTextsBlockProps {
  form: OptInFeatureFormData;
  language: OptInLanguage;
  json: string;
  jsonError: string | null;
  /** Everything still missing, across the three languages. Computed once, by the form's hook. */
  problems: string[];
  /** Error of the base name, raised by the save attempt rather than by this block. */
  nameError?: string;
  onLanguageChange: (language: OptInLanguage) => void;
  onTextChange: (field: keyof OptInTextsDraft, value: string) => void;
  /** The appearance has no language, so it does not go through `onTextChange`. */
  onAppearanceChange: <K extends keyof OptInAppearanceDraft>(
    field: K,
    value: OptInAppearanceDraft[K],
  ) => void;
  /** Neither does the toast type — it is the switch, and a switch has no language. */
  onToastChange: <K extends keyof OptInToastsDraft>(field: K, value: OptInToastsDraft[K]) => void;
  onJsonChange: (raw: string) => void;
  onPreview: (surface: OptInPreviewSurface) => void;
}

export const OptInTextsBlock = ({
  form,
  language,
  json,
  jsonError,
  problems,
  nameError,
  onLanguageChange,
  onTextChange,
  onAppearanceChange,
  onToastChange,
  onJsonChange,
  onPreview,
}: OptInTextsBlockProps): JSX.Element => {
  const toast = useActionToast();
  const [mode, setMode] = useState<'fields' | 'json'>('fields');
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);

  /**
   * Hands over a document to paste elsewhere: the empty template, or what is filled in right now,
   * which is how one cadastro seeds the next.
   */
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copiado`);
    } catch (error) {
      toast.failure(`Não foi possível copiar ${what.toLowerCase()}`, error);
    }
  };

  const texts = form.texts[language];

  return (
    <div className={wrapStyle}>
      <div className={titleRowStyle}>
        <div className={titleBlockStyle}>
          <h2 className={titleStyle}>Textos da funcionalidade</h2>
          <p className={subtitleStyle}>
            Edite pelos campos ou cole o JSON pronto. Os dois lados ficam sempre iguais.
          </p>
        </div>
        <div className={spacerStyle} />
        <UTButton
          fitContent
          variant='tertiary'
          size='small'
          id='opt-in-feature-schema'
          text='Ver schema'
          icon='Misc.Lightbulb'
          onClick={() => {
            setIsSchemaOpen(true);
          }}
        />
        <UTButton
          fitContent
          variant='tertiary'
          size='small'
          id='opt-in-feature-json-copy-template'
          text='Copiar template'
          icon='Communication.CopySimple'
          onClick={() => {
            void copy(createTextsTemplate(), 'Template');
          }}
        />
      </div>

      <div className={controlsStyle}>
        <Tag
          tone='primary'
          selected={mode === 'fields'}
          onClick={() => {
            setMode('fields');
          }}
        >
          Campos
        </Tag>
        <Tag
          tone='primary'
          selected={mode === 'json'}
          onClick={() => {
            setMode('json');
          }}
        >
          JSON
        </Tag>

        <span className={dividerStyle} />

        <span className={miniLabelStyle}>Idioma</span>
        {OPT_IN_LANGUAGES.map(entry => (
          <Tag
            key={entry}
            tone='primary'
            selected={language === entry}
            onClick={() => {
              onLanguageChange(entry);
            }}
          >
            {OPT_IN_LANGUAGE_LABELS[entry]}
          </Tag>
        ))}

        <div className={spacerStyle} />

        {jsonError ? (
          <span
            id='opt-in-feature-json-status'
            className={statusStyle}
            style={{ color: 'var(--color-danger)' }}
          >
            <GLIcon variant='Notifications.WarningCircle' size={14} fill='var(--color-danger)' />
            JSON inválido: {jsonError}
          </span>
        ) : (
          <span
            id='opt-in-feature-json-status'
            className={statusStyle}
            style={{ color: 'var(--color-success)' }}
          >
            <GLIcon variant='Communication.CheckCircle' size={14} fill='var(--color-success)' />
            JSON válido — 15 textos, 3 idiomas + aparência + toasts
          </span>
        )}
      </div>

      {problems.length > 0 ? (
        <div id='opt-in-feature-problems' className={pendingStyle}>
          <span className={pendingTitleStyle}>
            <GLIcon variant='Notifications.WarningCircle' size={14} fill='var(--gl-yellow-dark)' />
            Falta preencher antes de continuar
          </span>
          <ul className={pendingListStyle}>
            {problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {mode === 'json' ? (
        <div className={jsonModeStyle}>
          <div className={jsonActionsStyle}>
            <UTButton
              fitContent
              variant='tertiary'
              size='small'
              id='opt-in-feature-json-copy-current'
              text='Copiar este JSON'
              icon='Communication.CopySimple'
              onClick={() => {
                void copy(json, 'JSON dos textos');
              }}
            />
          </div>
          <textarea
            id='opt-in-feature-json-input'
            className={jsonAreaStyle}
            spellCheck={false}
            value={json}
            aria-label='JSON dos textos da funcionalidade'
            onChange={event => {
              onJsonChange(event.target.value);
            }}
          />
        </div>
      ) : (
        <>
          {OPT_IN_TEXT_SURFACES.map(surface => (
            <section
              key={surface.id}
              id={`opt-in-feature-surface-${surface.id}`}
              className={surfaceStyle}
            >
              <div className={surfaceHeadStyle}>
                <span className={surfaceIndexStyle}>{surface.index}</span>
                <span className={surfaceTitleStyle}>{surface.title}</span>
                <p className={surfaceHintStyle}>{surface.hint}</p>
                <div className={spacerStyle} />
                {surface.id === 'entry' ? null : (
                  <UTButton
                    fitContent
                    variant='secondary'
                    size='small'
                    id={`opt-in-feature-preview-${surface.id}`}
                    text='Pré-visualizar'
                    icon='Misc.Eye'
                    onClick={() => {
                      onPreview(surface.id === 'confirm' ? 'confirm' : 'optOut');
                    }}
                  />
                )}
              </div>

              <div className={fieldsGridStyle}>
                {surface.fields.map(field => (
                  <div
                    key={field.name}
                    className={`${fieldStyle} ${field.multiline ? proseFieldStyle : ''}`}
                    style={{ gridColumn: SPAN_BY_WIDTH[field.width] }}
                  >
                    <span className={monoLabelStyle}>
                      {field.name} <span className={fieldNameStyle}>· {field.label}</span>
                    </span>
                    {field.multiline ? (
                      <UTTextArea
                        id={`opt-in-feature-text-${field.name}`}
                        textAreaSize='small'
                        value={texts[field.name]}
                        onChange={event => {
                          onTextChange(field.name, event.target.value);
                        }}
                      />
                    ) : (
                      <UTTextField
                        id={`opt-in-feature-text-${field.name}`}
                        value={texts[field.name]}
                        state={field.name === 'name' && nameError ? 'error' : 'normal'}
                        onChange={event => {
                          onTextChange(field.name, event.target.value);
                        }}
                      />
                    )}
                    {field.name === 'name' && nameError ? (
                      <span className={errorStyle}>{nameError}</span>
                    ) : null}
                  </div>
                ))}
              </div>

              {surface.id === 'entry' ? (
                <>
                  <OptInEntryAppearanceFields
                    appearance={form.appearance}
                    onChange={onAppearanceChange}
                  />
                  <OptInEntryPreview
                    name={texts.name}
                    description={texts.description}
                    enterCta={texts.enterCta}
                    exitCta={texts.exitCta}
                    badgeTooltip={texts.badgeTooltip}
                    appearance={form.appearance}
                  />
                </>
              ) : null}

              {/*
                The toast of this surface, under the modal that fires it — the last frame of the
                same story, not a block of its own at the foot of the form.
              */}
              {OPT_IN_TOAST_SIDES.filter(side => side.surface === surface.id).map(side => (
                <OptInToastFields
                  key={side.id}
                  side={side}
                  toasts={form.toasts}
                  texts={texts}
                  onToastChange={onToastChange}
                  onTextChange={onTextChange}
                />
              ))}
            </section>
          ))}
        </>
      )}

      {isSchemaOpen ? (
        <SchemaDialog
          id='opt-in-feature-schema-modal'
          title='Schema dos textos'
          description='O JSON cobre só o conteúdo — BU, chave, use cases e o estado da funcionalidade ficam na identificação.'
          schema={createTextsTemplate()}
          prompt={createTextsPrompt()}
          promptHint='Cole numa IA junto com a descrição da funcionalidade e traga o JSON pronto, nos três idiomas.'
          onClose={() => {
            setIsSchemaOpen(false);
          }}
        >
          Campos ausentes viram vazios, e o documento traz os três idiomas de cada texto, que é a
          razão de colar um em vez de percorrer as abas quinze vezes. Os blocos{' '}
          <code>appearance</code> e <code>toasts</code> não têm idioma e vêm uma vez só; ausentes,
          voltam ao padrão — ícone e selo por conta do dashboard, o convite aparecendo, e nenhum dos
          dois toasts disparando.
        </SchemaDialog>
      ) : null}
    </div>
  );
};
